/**
 * External Auth Client for 1Anime External OAuth2 Provider
 * AUTH URL: https://auth.1anime.app
 *
 * Implements OAuth2 Authorization Code flow with the external auth service.
 */

import { fetchWithTimeout } from './fetch-with-timeout';

const AUTH_BASE_URL = 'https://auth.1anime.app';

// Environment variables (set in .env)
// NEXT_PUBLIC_AUTH_CLIENT_ID - OAuth2 client ID
// AUTH_CLIENT_SECRET - OAuth2 client secret (server-side only)
// NEXT_PUBLIC_AUTH_REDIRECT_URI - Callback URL (e.g., https://1anime.app/api/auth/callback)

/**
 * Session storage keys
 * Using better-auth session cookie name for compatibility with auth server
 */
export const SESSION_COOKIE_NAME = 'better-auth.session_token';
export const STATE_COOKIE_NAME = '1anime_auth_state';

export interface ExternalAuthUser {
  id: string;
  anilistId?: number;
  username: string;
  email?: string;
  avatarUrl?: string;
  bannerUrl?: string;
  role: 'user' | 'admin';
  createdAt: string;
  updatedAt: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  scope: string;
}

export interface UserInfoResponse {
  id?: string;
  sub?: string;
  user_id?: string;
  anilist_id?: number;
  username?: string;
  name?: string;
  email?: string;
  avatar_url?: string;
  avatar?: string;
  banner_url?: string;
  banner?: string;
  role?: 'user' | 'admin';
  created_at?: string;
  updated_at?: string;
}

// Available scopes for OAuth2
export type AuthScope =
  | 'identify'
  | 'email'
  | 'profile'
  | 'lists'
  | 'write'
  | 'mei'
  | 'openid';

/**
 * Generates a cryptographically secure state parameter
 * Works on both client and server side
 */
export function generateState(): string {
  // Browser environment - use Web Crypto API
  if (typeof window !== 'undefined' && window.crypto) {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join(
      ''
    );
  }
  // Server-side / Node.js environment - use globalThis.crypto (available in Node 19+)
  if (typeof globalThis !== 'undefined' && globalThis.crypto) {
    const array = new Uint8Array(32);
    globalThis.crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join(
      ''
    );
  }
  // Fallback for older environments - generates a random string
  // Not cryptographically secure but sufficient for OAuth state parameter
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).slice(2, 15);
  const randomPart2 = Math.random().toString(36).slice(2, 15);
  return `${timestamp}${randomPart}${randomPart2}`;
}

// Default scopes requested by the 1Anime frontend.
// MUST stay in sync with the 1Auth OAuth client configuration:
// identify,email,profile,lists,write
const DEFAULT_SCOPES: AuthScope[] = [
  'identify',
  'email',
  'profile',
  'lists',
  'write',
];

/**
 * Generates the OAuth2 authorization URL for redirecting users to login
 */
export function getAuthorizationUrl(options?: {
  scopes?: AuthScope[];
  state?: string;
  redirectUri?: string;
}): string {
  const clientId = process.env.NEXT_PUBLIC_AUTH_CLIENT_ID;
  const redirectUri =
    options?.redirectUri ||
    process.env.NEXT_PUBLIC_AUTH_REDIRECT_URI ||
    `${typeof window === 'undefined' ? '' : window.location.origin}/api/auth/callback`;

  if (!clientId) {
    throw new Error('NEXT_PUBLIC_AUTH_CLIENT_ID is not configured');
  }

  const scopes = options?.scopes || DEFAULT_SCOPES;
  const state = options?.state || generateState();

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: scopes.join(' '),
    state,
  });

  return `${AUTH_BASE_URL}/oauth/authorize?${params.toString()}`;
}

/**
 * Exchanges authorization code for access token (server-side only)
 */
export async function exchangeCodeForToken(
  code: string,
  redirectUri?: string
): Promise<TokenResponse> {
  const clientId = process.env.NEXT_PUBLIC_AUTH_CLIENT_ID;
  const clientSecret = process.env.AUTH_CLIENT_SECRET;
  const callbackUri =
    redirectUri ||
    process.env.NEXT_PUBLIC_AUTH_REDIRECT_URI ||
    `${process.env.NEXT_PUBLIC_SITE_URL || ''}/api/auth/callback`;

  if (!clientId || !clientSecret) {
    throw new Error('AUTH_CLIENT_ID or AUTH_CLIENT_SECRET is not configured');
  }

  const response = await fetchWithTimeout(`${AUTH_BASE_URL}/oauth/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: clientId,
      client_secret: clientSecret,
      code,
      redirect_uri: callbackUri,
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Token exchange failed: ${error}`);
  }

  return response.json();
}

/**
 * Refreshes access token using refresh token (server-side only)
 */
export async function refreshAccessToken(
  refreshToken: string
): Promise<TokenResponse> {
  const clientId = process.env.NEXT_PUBLIC_AUTH_CLIENT_ID;
  const clientSecret = process.env.AUTH_CLIENT_SECRET;

  if (!clientId || !clientSecret) {
    throw new Error('AUTH_CLIENT_ID or AUTH_CLIENT_SECRET is not configured');
  }

  const response = await fetchWithTimeout(`${AUTH_BASE_URL}/oauth/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: clientId,
      client_secret: clientSecret,
      refresh_token: refreshToken,
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Token refresh failed: ${error}`);
  }

  return response.json();
}

/**
 * Fetches user info using access token
 */
export async function getUserInfo(
  accessToken: string
): Promise<UserInfoResponse> {
  const response = await fetchWithTimeout(`${AUTH_BASE_URL}/userinfo`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to fetch user info: ${error}`);
  }

  return response.json();
}

/**
 * Converts external API user response to internal format
 * Handles various field name formats that the auth API might return
 */
export function normalizeUser(userInfo: UserInfoResponse): ExternalAuthUser {
  // Get user ID from various possible fields
  const userId = userInfo.id || userInfo.sub || userInfo.user_id;
  if (!userId) {
    // eslint-disable-next-line no-console
    console.error('User info missing ID field:', JSON.stringify(userInfo));
    throw new Error('User info missing required ID field');
  }

  return {
    id: userId,
    anilistId: userInfo.anilist_id,
    username: userInfo.username || userInfo.name || 'User',
    email: userInfo.email,
    avatarUrl: userInfo.avatar_url || userInfo.avatar,
    bannerUrl: userInfo.banner_url || userInfo.banner,
    role: userInfo.role || 'user',
    createdAt: userInfo.created_at || new Date().toISOString(),
    updatedAt: userInfo.updated_at || new Date().toISOString(),
  };
}

/**
 * Validates an access token by calling userinfo endpoint
 */
export async function validateToken(
  accessToken: string
): Promise<ExternalAuthUser | null> {
  try {
    const userInfo = await getUserInfo(accessToken);
    return normalizeUser(userInfo);
  } catch {
    return null;
  }
}

/**
 * Sign in with email and password (OTP-based)
 * Uses better-auth email OTP flow
 */
export async function signInWithEmail(
  email: string,
  password: string
): Promise<{
  user: ExternalAuthUser;
  session: {
    id: string;
    expiresAt: string;
    createdAt: string;
  };
}> {
  const response = await fetchWithTimeout(
    `${AUTH_BASE_URL}/api/auth/sign-in/email`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: 'Sign in failed' }));
    throw new Error(error.error_description || error.error || 'Sign in failed');
  }

  return response.json();
}

/**
 * Sign up with email and password
 */
export async function signUpWithEmail(
  email: string,
  password: string,
  name?: string
): Promise<{
  user: ExternalAuthUser;
  session: {
    id: string;
    expiresAt: string;
    createdAt: string;
  };
}> {
  const response = await fetchWithTimeout(
    `${AUTH_BASE_URL}/api/auth/sign-up/email`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password, name }),
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: 'Sign up failed' }));
    throw new Error(error.error_description || error.error || 'Sign up failed');
  }

  return response.json();
}

/**
 * Unlink a social account from the current user's profile
 */
export async function unlinkAccount(
  provider: 'discord' | 'google' | 'anilist',
  sessionToken: string
): Promise<{ success: boolean }> {
  const response = await fetchWithTimeout(
    `${AUTH_BASE_URL}/api/unlink/${provider}`,
    {
      method: 'POST',
      headers: {
        Cookie: `${SESSION_COOKIE_NAME}=${sessionToken}`,
      },
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: 'Unlink failed' }));
    throw new Error(error.error_description || error.error || 'Unlink failed');
  }

  return response.json();
}

/**
 * Revoke access for a specific OAuth client/app
 */
export async function revokeClient(
  clientId: string,
  sessionToken: string
): Promise<{ success: boolean }> {
  const response = await fetchWithTimeout(
    `${AUTH_BASE_URL}/api/revoke/${clientId}`,
    {
      method: 'POST',
      headers: {
        Cookie: `${SESSION_COOKIE_NAME}=${sessionToken}`,
      },
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: 'Revoke failed' }));
    throw new Error(error.error_description || error.error || 'Revoke failed');
  }

  return response.json();
}

/**
 * Register a new OAuth application
 * Requires authentication via Bearer token
 */
export async function registerApp(
  accessToken: string,
  appData: {
    name: string;
    redirect_uris: string[];
    scopes: string[];
  }
): Promise<{
  client_id: string;
  client_secret: string;
  name: string;
  redirect_uris: string[];
  scopes: string[];
}> {
  const response = await fetchWithTimeout(`${AUTH_BASE_URL}/apps/register`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(appData),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: 'Registration failed' }));
    throw new Error(
      error.error_description || error.error || 'Registration failed'
    );
  }

  return response.json();
}

/**
 * Get server health status
 */
export async function getHealth(): Promise<{
  status: string;
  timestamp: string;
}> {
  const response = await fetchWithTimeout(`${AUTH_BASE_URL}/health`);

  if (!response.ok) {
    throw new Error('Health check failed');
  }

  return response.json();
}

/**
 * Get server info and available endpoints
 */
export async function getServerInfo(): Promise<{
  name: string;
  version: string;
  status: string;
  endpoints: {
    authorize: string;
    token: string;
    userinfo: string;
    register: string;
  };
}> {
  const response = await fetchWithTimeout(`${AUTH_BASE_URL}/api/info`);

  if (!response.ok) {
    throw new Error('Failed to fetch server info');
  }

  return response.json();
}

/**
 * Client-side session interface
 */
export interface ClientSession {
  data: {
    user: ExternalAuthUser;
    session: {
      expiresAt: string;
      createdAt: string;
    };
  } | null;
  isPending: boolean;
  error: Error | null;
}
